// Lazy-loaded html2canvas screenshot capture. Dynamic-imported only when the
// user clicks Report Issue so the initial bundle isn't bloated by the ~50KB
// lib. First capture pays a one-time ~200ms cost to download the chunk;
// subsequent captures are instant.

export interface ScreenshotResult {
  blob: Blob;
  dataUrl: string;
  width: number;
  height: number;
}

export async function captureScreenshot(): Promise<ScreenshotResult | null> {
  if (typeof window === 'undefined' || typeof document === 'undefined') return null;
  try {
    // @ts-ignore — html2canvas is an optional dep; treat missing as graceful no-op
    const mod = await import('html2canvas').catch(() => null);
    if (!mod) {
      console.warn('[issueReporter] html2canvas not installed; skipping screenshot');
      return null;
    }
    const html2canvas = (mod as any).default || mod;

    const target = document.body;
    const canvas: HTMLCanvasElement = await html2canvas(target, {
      logging: false,
      // Leave the help launcher out of its own bug-report screenshot.
      ignoreElements: (el: Element) => el.hasAttribute('data-help-launcher'),
      useCORS: true,
      // Cap the captured area so we don't blow past S3 / template limits on
      // very tall scrolling pages.
      width: Math.min(window.innerWidth, 1600),
      height: Math.min(window.innerHeight, 1200),
      x: window.scrollX,
      y: window.scrollY,
      scale: window.devicePixelRatio > 1 ? 1 : 1, // keep payload small
    });

    const dataUrl = canvas.toDataURL('image/png');
    const blob: Blob = await new Promise((resolve, reject) =>
      canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('blob failed'))), 'image/png'),
    );
    return { blob, dataUrl, width: canvas.width, height: canvas.height };
  } catch (e) {
    console.warn('[issueReporter] screenshot failed:', e);
    return null;
  }
}
